import React from 'react'
import styled from 'styled-components'
import { Typography } from '@material-ui/core'
import { Button } from 'sputnik-ui'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { fixStreamCamera } from 'features/Cameras/camerasSlice'
import trackGoal from 'utils/metrika'

const InfoWrapper = styled.div`
  margin-top: 24px;
`

const ActionsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const DetailLabel = styled(Typography)`
  && {
    font-family: 'Open Sans', sans-serif;
    font-size: 12px;
    color: #8f9bb3;
  }
`

const DetailValue = styled(Typography)`
  && {
    font-family: 'Inter', 'Open Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #051125;
    display: flex;
    align-items: center;
    gap: 6px;
  }
`

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => (p.online ? '#0FBA81' : '#FF3D71')};
  display: inline-block;
`

function CameraInfo({ camera }) {
  const dispatch = useDispatch()
  const { t } = useTranslation(['cameras_view'])

  if (!camera) return null

  const handleFixStream = async () => {
    trackGoal('control_cameras_fix_stream_click', {
      camera_uuid: camera.uuid,
      is_online: Boolean(camera.is_online),
    })

    const action = await dispatch(fixStreamCamera(camera.uuid))
    const success = fixStreamCamera.fulfilled.match(action)
    const errorCode = success
      ? undefined
      : action?.payload || action?.error?.message || 'unknown_error'

    trackGoal('control_cameras_fix_stream_result', {
      camera_uuid: camera.uuid,
      result: success ? 'success' : 'failed',
      error_code: errorCode,
    })
  }

  const archiveDepth = camera.services?.find((s) => s.startsWith('archive_'))
  const depthDays = archiveDepth ? archiveDepth.replace('archive_', '') : null

  return (
    <InfoWrapper>
      {camera.embed && (
        <ActionsRow>
          <Button secondary onClick={handleFixStream}>
            {t('cameras_view:fix_stream')}
          </Button>
        </ActionsRow>
      )}

      <DetailsGrid>
        <DetailItem>
          <DetailLabel>{t('cameras_view:serial_number')}</DetailLabel>
          <DetailValue>{camera.serial_number || '—'}</DetailValue>
        </DetailItem>
        <DetailItem>
          <DetailLabel>{t('cameras_view:status')}</DetailLabel>
          <DetailValue>
            <StatusDot online={camera.is_online} />
            {camera.is_online
              ? t('cameras_view:online')
              : t('cameras_view:offline')}
          </DetailValue>
        </DetailItem>
        {camera.model && (
          <DetailItem>
            <DetailLabel>{t('cameras_view:model')}</DetailLabel>
            <DetailValue>{camera.model}</DetailValue>
          </DetailItem>
        )}
        {camera.fwVersion && (
          <DetailItem>
            <DetailLabel>{t('cameras_view:firmware')}</DetailLabel>
            <DetailValue>{camera.fwVersion}</DetailValue>
          </DetailItem>
        )}
        {depthDays && (
          <DetailItem>
            <DetailLabel>{t('cameras_view:archive_depth')}</DetailLabel>
            <DetailValue>
              {depthDays} {t('cameras_view:days')}
            </DetailValue>
          </DetailItem>
        )}
        {(camera.address || camera.short_address) && (
          <DetailItem>
            <DetailLabel>{t('cameras_view:address')}</DetailLabel>
            <DetailValue>{camera.address || camera.short_address}</DetailValue>
          </DetailItem>
        )}
      </DetailsGrid>
    </InfoWrapper>
  )
}

export default CameraInfo
